import { Button } from "@floreysoft/button";
import { css, CSSResult, html, LitElement, TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from "lit/directives/style-map.js";
import { classMap } from "lit/directives/class-map.js";

export const SCROLLBAR_STYLE = css`
::-webkit-scrollbar {
    width: .75em;
}
::-webkit-scrollbar-track {
    background-color: var(--fs-background-color);
}
::-webkit-scrollbar-thumb {
    background-color: var(--fs-widget-background-color-hover);
}
::-webkit-scrollbar-thumb:hover {
    background-color: var(--fs-widget-background-color-selected);
}`

export class DialogButton {
    label?: string
    id?: string
    disabled?: boolean
    theme?: string
}

@customElement("fs-dialog")
export class Dialog extends LitElement {
    @property({ type: Boolean, reflect: true })
    set open(open: boolean) {
        this._open = open
        this.requestUpdate()
        if (open && this._dialog) {
            this._dialog.style.display = "flex"
        }
    }
    get open() {
        return this._open
    }

    @property() header: string | undefined
    @property() theme: string = ""
    @property({ type: Object }) context: any
    @property() width: string | undefined
    @property() height: string | undefined
    @property() selectedOptionOnReturn?: string
    @query(".dialog") _dialog?: HTMLDialogElement
    @query(".dialogWrapper") _dialogWrapper?: HTMLDialogElement

    @property({ type: Array })
    set buttons(buttons: any[]) {
        this._buttons = buttons
        for (let button of this.buttons) {
            if (typeof button == "string") {
                if (typeof this._selectedOption == "undefined") {
                    this._selectedOption = button
                }
            } else {
                if (button.theme && button.theme == "primary") {
                    this._selectedOption = (button.id ? button.id : button.label)
                    break
                } else if (typeof this._selectedOption == "undefined") {
                    this._selectedOption = (button.id ? button.id : button.label)
                }
            }
        }
        this.requestUpdate()
    }

    get buttons(): any[] {
        return this._buttons!
    }

    @query("fs-button[theme='primary']")
    private primaryButton?: Button

    private x?: number
    private y?: number
    private dragging?: boolean
    private _buttons?: any[]
    private _selectedOption: string | undefined
    private _open: boolean = false

    private readonly KEYCODE = {
        ESCAPE: 27,
        RETURN: 13
    }

    static get styles() {
        return [SCROLLBAR_STYLE, css`
        :host {
        }
        .backdrop, .dialogWrapper {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 8;
        }
        .dialogWrapper {
            z-index: 9;
        }
        .backdrop {
            opacity: 0;
            background: var(--fs-backdrop-color, rgba(0, 0, 0, .1));
            transition: all ease 0.12s;
        }
        .dialog {
            position: relative;
            display: flex;
            flex-direction: column;
            opacity: 0;
            background-color: var(--fs-background-color);
            min-width: 150px;
            max-width: 100%;
            max-height: 100%;
            box-shadow: var(--fs-box-shadow);
        }
        .backdrop.open {
            opacity: 1;
            pointer-events: auto;
        }
        .dialog.open {
            opacity: 1;
            pointer-events: auto;
            transition-timing-function: cubic-bezier(.12, .32, .54, 1.5);
        }
        header {
            color: var(--fs-accent-color-text);
            background-color: var(--fs-accent-color, inherit);
            padding:  var(--fs-space-wide);
            cursor: move;
        }
        header.success {
            background-color: var(--fs-widget-background-color-success);
        }
        header.error {
            background-color: var(--fs-widget-background-color-error);
            color: var(--fs-text-color-error);
        }
        section {
            display: block;
            background-color: var(--fs-background-color);
            overflow-y: auto;
            max-width: 100%;
            box-sizing: border-box;
        }
        footer {
            padding: var(--fs-space-wide);
            text-align: right;
        }

        .buttons {
            display: inline-grid;
            grid-auto-flow: column;
            justify-self: right;
            column-gap: 0.2em;
        }`]
    }

    render() {
        return this.buttons ? html`
        <div class="backdrop ${this.open ? "open" : ""}"></div>
        <focus-trap>
            <div class="dialogWrapper" @mouseup=${this.endDrag} @mousemove=${this.drag}>
                <div class=${classMap({ dialog: true, open: this.open })}>
                        ${this.header ? html`<header part="header" class="${this.theme}" @mousedown=${this.startDrag}>${this.header}</header>` : undefined}
                        <section part="content" @keydown="${this.keyDown}" style=${styleMap({ width: this.width || "auto", height: this.height || "auto" })}><slot></slot></section>
                        <footer part="footer" @keydown="${this.keyDown}"><div class="buttons">${this.buttons.map(button => html`<fs-button theme="${ifDefined(typeof button == "string" ? "primary" : button.theme)}" ?disabled="${button.disabled}" @click="${(e: Event) => this.optionSelected((typeof button == "string" ? button : (button.id ? button.id : button.label)), this.context)}">${typeof button == "string" ? button : button.label}</fs-button>`)}</div></footer>
                </div>
            </div>
        </focus-trap>`
            : undefined
    }

    focus() {
        if (this.primaryButton) this.primaryButton.focus()
    }

    optionSelected(option: string | undefined, context: any) {
        this.open = false
        if (this.dispatchEvent(new CustomEvent('selected', { detail: { option, context }, cancelable: true }))) {
            setTimeout(() => {
                // Only set display to none if dialog has not been opened again in the meantime
                if (!this.open && this._dialog) {
                    this._dialog.style.display = "none"
                }
            }, 500)
        }
    }

    protected startDrag(e: MouseEvent) {
        if (this._dialog && this._dialogWrapper) {
            let left = this._dialog.style.left ? parseInt(this._dialog.style.left, 10) : 0
            let top = this._dialog.style.top ? parseInt(this._dialog.style.top, 10) : 0
            this.x = e.screenX - left
            this.y = e.screenY - top
            this.dragging = true;
            this._dialogWrapper.style.pointerEvents = "auto"
        }
    }

    protected endDrag(e: MouseEvent) {
        this.dragging = false;
        if (this._dialogWrapper) {
            this._dialogWrapper.style.pointerEvents = "none"
        }
    }

    protected drag(e: MouseEvent) {
        e.preventDefault
        e.stopPropagation
        if (this.dragging && this._dialog && this.x && this.y) {
            this._dialog.style.left = (e.screenX - this.x) + "px"
            this._dialog.style.top = (e.screenY - this.y) + "px"
        }
    }

    private keyDown(e: KeyboardEvent) {
        switch (e.keyCode) {
            case this.KEYCODE.RETURN:
                e.preventDefault()
                e.stopPropagation()
                this.optionSelected(this.selectedOptionOnReturn ? this.selectedOptionOnReturn : this._selectedOption, this.context)
                break;
            case this.KEYCODE.ESCAPE:
                e.preventDefault()
                e.stopPropagation()
                this.optionSelected(undefined, this.context)
                break;
        }
    }

}